/**
 * ONE LOADER FOR A SITE'S OVERVIEW (Hamzah, 2026-10-19: "have one loader", over a screenshot of
 * the bank Overview's first open — six cards each shimmering on its own, Needs a look already
 * counting alarms under its shimmer, and the Load chart saying "no feeder reports this yet" before
 * a single reading had arrived).
 *
 * Every card fetches its own data, so on a first open they finished one by one, and the page
 * assembled itself in front of the reader. Now the page holds ONE loader until every first fetch
 * in flight has finished, then shows the cards together. "First" is TanStack Query's `pending`
 * — no data yet — so a background refresh (`success`, refetching) never brings the loader back:
 * once a site is shown, its numbers stay on screen while they update.
 *
 * Two guards. The count must stay at zero for `SETTLE_MS`, because a card starts its second fetch
 * only once its first answered (the Load chart asks for its curves after the feeders are known), and
 * the moment between the two is a zero that means nothing. And the page is shown after
 * `MAX_WAIT_MS` whatever is still loading, so one slow or retrying fetch can never hold the whole
 * Overview: past that, the cards that are still waiting say so themselves, as they always did.
 *
 * The count is the whole client's, not this page's: the header's and the rail's fetches for a
 * newly picked site start at the same moment and finish in the same few hundred milliseconds, and
 * filtering by query key would need a list of this page's keys that the next card would silently
 * fall out of.
 */
import { useEffect, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';

export const SETTLE_MS = 250;
export const MAX_WAIT_MS = 8_000;

/**
 * Whether the page may show. `idleForMs` is how long no first fetch has been in flight (null while
 * one is); `openForMs` how long ago the site was opened.
 */
export function firstLoadDone(o: { idleForMs: number | null; openForMs: number; settleMs?: number; maxMs?: number }): boolean {
  const settle = o.settleMs ?? SETTLE_MS;
  const max = o.maxMs ?? MAX_WAIT_MS;
  return o.openForMs >= max || (o.idleForMs !== null && o.idleForMs >= settle);
}

/**
 * True once the page opened for `key` has finished its first load; false again for a new key. No key
 * (nothing to gate) is always true.
 */
export function useFirstLoad(key: string | undefined): boolean {
  const pending = useIsFetching({ predicate: (q) => q.state.status === 'pending' });
  const [doneFor, setDoneFor] = useState<string | null>(null);
  const done = key === undefined || doneFor === key;

  /* Idle: wait out the settle, then show. A fetch starting in the meantime cancels the wait. */
  useEffect(() => {
    if (done || key === undefined || pending > 0) return undefined;
    const t = setTimeout(() => {
      if (firstLoadDone({ idleForMs: SETTLE_MS, openForMs: 0 })) setDoneFor(key);
    }, SETTLE_MS);
    return () => clearTimeout(t);
  }, [done, key, pending]);

  /* However long anything takes, the page shows after the cap. */
  useEffect(() => {
    if (done || key === undefined) return undefined;
    const t = setTimeout(() => {
      if (firstLoadDone({ idleForMs: null, openForMs: MAX_WAIT_MS })) setDoneFor(key);
    }, MAX_WAIT_MS);
    return () => clearTimeout(t);
  }, [done, key]);

  return done;
}

/**
 * THE LOADER'S LAST MOMENT (2026-10-20, `RingLoader.tsx`). The loader does not vanish the frame
 * the data is in: it resolves — the screen flashes, the reading lands — for `resolveMs`, and only
 * then do the cards come in. Shown only for a site whose loader was actually up: one opened from the
 * cache, done on its first render, goes straight to the cards with no resolve to watch.
 *
 * `shownFor` is the key the loader was last up for, `settledFor` the key whose resolve has finished.
 */
export interface LoaderPhase {
  /** The loader is on screen. */
  loader: boolean;
  /** It is resolving: the data is in, the cards wait. */
  resolving: boolean;
  /** The cards are shown. */
  cards: boolean;
}

export function loaderPhase(o: { key: string | undefined; done: boolean; shownFor: string | null; settledFor: string | null }): LoaderPhase {
  if (o.key === undefined) return { loader: false, resolving: false, cards: true };
  if (!o.done) return { loader: true, resolving: false, cards: false };
  if (o.shownFor === o.key && o.settledFor !== o.key) return { loader: true, resolving: true, cards: false };
  return { loader: false, resolving: false, cards: true };
}

export function useLoaderPhase(done: boolean, key: string | undefined, resolveMs: number): LoaderPhase {
  const [shownFor, setShownFor] = useState<string | null>(null);
  const [settledFor, setSettledFor] = useState<string | null>(null);

  useEffect(() => {
    if (key !== undefined && !done) setShownFor(key);
  }, [key, done]);

  useEffect(() => {
    if (!done || key === undefined || shownFor !== key || settledFor === key) return undefined;
    const t = setTimeout(() => setSettledFor(key), resolveMs);
    return () => clearTimeout(t);
  }, [done, key, shownFor, settledFor, resolveMs]);

  return loaderPhase({ key, done, shownFor, settledFor });
}
